import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  StrapiAuthenticationResponse,
  StrapiResetPasswordData,
  StrapiResponse
} from 'strapi-sdk-js';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { authAtom } from '../../store/jotai/atoms/auth.atom';
import { StrapiUser } from '../models/strapi-user.model';
import { StrapiEntity } from '../../constants/StrapiEntity';
import { StrapiRegisterUserRequest } from '../models/request/strapi-register-user-request.model';
import { createImageUploadFormData } from '../../utils/imageUtils';
import { GeocodeAddressState } from '../../store/jotai/atoms/coords.atom';
import { StrapiRequestMail } from '../models/request/strapi-request-mail.model';
import { StrapiUserRequest } from '../models/request/strapi-user-request.model';
import { StrapiRequest } from '../models/request/strapi-request.model';
import { StrapiFetchResponse } from '../models/response/strapi-list-response.model';
import { StrapiImage } from '../models/strapi-image.model';
import { StrapiOrder } from '../models/strapi-order.model';
import { Logger } from '../logger/Logger';
import { captureException, setSentryUser } from '../../utils/sentry';
import {
  StrapiOrderStatusEnum,
  StrapiStatus,
  StrapiVendorOrderStatusEnum
} from '../models/strapi-status.model';
import { StrapiPayout, StrapiPayoutStatus } from '../models/strapi-payout.model';
import { StrapiVendor } from '../models/strapi-vendor.model';
import { StrapiConversation, ConversationsState } from '../models/strapi-conversation.model';
import { StrapiChatMessage } from '../models/strapi-chat-message.model';

// The session JWT lives in a backend-set httpOnly cookie (not JS-readable).
// Sending credentials on every request lets the browser attach it automatically
// and store the Set-Cookie returned by login/register/logout.
axios.defaults.withCredentials = true;

// Clears the httpOnly session cookie server-side. Uses fetch with keepalive
// (not axios) so the request completes even when the page unloads immediately
// after — a hard redirect or tab close won't cancel it and leave a stale cookie.
const postLogout = (): void => {
  fetch(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true
  }).catch((error) => Logger.error('Logout request failed:', error));
};

// A previously-authenticated session received a 401 (expired/invalid JWT):
// clear client auth state, drop the now-stale cookie server-side, and send the
// user to login. Guarded so concurrent failing requests only trigger it once;
// the flag resets naturally on the ensuing full reload.
let sessionExpiryHandled = false;
const handleSessionExpiry = (): void => {
  if (sessionExpiryHandled || window.location.pathname.startsWith('/login')) {
    return;
  }
  sessionExpiryHandled = true;

  regionStore.set(userAtom, null);
  regionStore.set(authAtom, 'unauthenticated');
  setSentryUser(null);
  postLogout();
  window.location.assign('/login');
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status >= 500) {
      captureException(error, {
        url: error?.config?.url,
        method: error?.config?.method,
        status
      });
    }

    // Session expiry — only 401 (authentication failed). 403 is intentionally
    // excluded: it also means "authenticated but forbidden", so logging out on
    // 403 would evict authorized users hitting a permission-denied endpoint.
    if (status === 401 && regionStore.get(authAtom) === 'authenticated') {
      handleSessionExpiry();
    }

    return Promise.reject(error);
  }
);

let inflightLoadUser: Promise<StrapiUser | null> | null = null;

const strapiClient: StrapiClient = {
  register: async (data: StrapiRegisterUserRequest): Promise<string> => {
    try {
      const {
        firstName,
        lastName,
        email,
        password,
        howDidYouHearAboutUs,
        bekannte,
        newsletterOptIn
      } = data;

      const regUser = await axios.post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/local/register`,
        {
          identifier: email,
          password,
          firstName,
          lastName,
          howDidYouHearAboutUs,
          bekannte,
          newsletterOptIn,
          email,
          username: email
        },
        getAxiosHeaders(process.env.REACT_APP_STRAPI_REGISTRATION_TOKEN)
      );

      return Promise.resolve(regUser.data.jwt);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  login: (email: string, password: string) =>
    axios.post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/local`, {
      identifier: email,
      password
    }),
  loadUser: async (options?: { skipStripe?: boolean }) => {
    const skipStripe = options?.skipStripe ?? true;

    if (inflightLoadUser) return inflightLoadUser;

    const doLoad = async (): Promise<StrapiUser | null> => {
      try {
        // The httpOnly session cookie authenticates the call.
        const params = skipStripe ? '?skipStripe=true' : '';
        const response: AxiosResponse<StrapiResponse<StrapiUser>> = await axios.get(
          `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users/me${params}`
        );

        if (!response?.data?.data) {
          throw new Error('User data not found');
        }

        setUser(response.data.data);

        return response.data.data;
      } catch (error) {
        // 401/403 just means "not logged in" (no/invalid cookie) — a normal
        // resolve(null), not an error to propagate.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          return null;
        }
        return Promise.reject(error);
      }
    };

    inflightLoadUser = doLoad().finally(() => {
      inflightLoadUser = null;
    });
    return inflightLoadUser;
  },
  checkLogin: async (email: string, password: string) =>
    axios.post(
      `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/compat/e`,
      {
        u: email,
        p: password
      },
      getAxiosHeaders(process.env.REACT_APP_STRAPI_COMPAT_TOKEN)
    ),
  delete: (id: number, entityPath: string) => {
    try {
      return axios.delete(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/${entityPath}/${id}`,
        getAxiosHeaders()
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },
  create: async <T, S>(entityPath: EntityPath, entity: T, token?: string): Promise<S> => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/${entityPath}`,
        { data: entity },
        getAxiosHeaders(token)
      );
      return response.data as S;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  update: async <T, S>(entityPath: EntityPath, id: number, entity: T): Promise<S> => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/${entityPath}/${id}`,
        { data: entity },
        getAxiosHeaders()
      );
      return response.data as S;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  updateUser: async (updateData: Partial<StrapiUserRequest>) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/me`,
        updateData,
        getAxiosHeaders()
      );

      if (response?.data?.data) {
        setUser(response.data.data);
      }

      return response?.data?.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  fetch: async <T>(data: StrapiRequest): Promise<T> => {
    try {
      const { entityPath, id, populate, sort, filter, page, pageSize } = data;

      // `sort` and `filter` arrive as pre-formatted query fragments, so the
      // parts are joined by hand rather than via URLSearchParams (which would
      // escape them). Joining a filtered list also fixes the previous
      // concatenation, where `filter` was always prefixed with `&` and produced
      // `/api/orders&filter=…` whenever it was the only parameter.
      const query = [
        populate && `populate=${populate}`,
        sort,
        filter,
        page && `page=${page}`,
        pageSize && `pageSize=${pageSize}`
      ]
        .filter(Boolean)
        .join('&');

      const response = await axios.get(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/${entityPath}${
          id !== undefined && id !== null ? '/' + id : ''
        }${query ? `?${query}` : ''}`,
        getAxiosHeaders()
      );
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  sendRequestMail: async (data: StrapiRequestMail): Promise<void> => {
    try {
      await axios.post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/email/send-request-mail`,
        data,
        getAxiosHeaders()
      );
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  resetPassword: (email: string) =>
    axios.post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/forgot-password`, {
      email
    }),
  setNewPassword: (password: string, passwordConfirmation: string, code: string) =>
    axios.post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/reset-password`, {
      password,
      passwordConfirmation,
      code
    }),
  // Two-step, confirmed email change: the address is only recorded until the
  // link mailed to it is opened, so nothing here changes the account yet.
  requestEmailChange: (email: string) =>
    axios.post(
      `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/me/email-change`,
      { email },
      getAxiosHeaders()
    ),
  // No session needed — the token from the mail is the credential, and the link
  // is often opened in a browser that is not logged in.
  confirmEmailChange: (token: string) =>
    axios.post(
      `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/email-change/confirm`,
      { token }
    ),
  // Strapi verifies `currentPassword` itself, so this replaces the former
  // "log in again to check the old password, then PATCH the user" pair — which
  // never changed anything, since PATCH /users/me drops `password`.
  changePassword: (currentPassword: string, password: string, passwordConfirmation: string) =>
    axios.post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/auth/change-password`, {
      currentPassword,
      password,
      passwordConfirmation
    }),
  viesCheckStatus: () =>
    axios
      .get(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vies/check-status`)
      .then((response) => response.data.vow.available as boolean)
      .catch((error) => error),
  viesCheckVatNumber: (fullVatNumber: string) =>
    axios.post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vies/check-vat-number`, {
      fullVatNumber
    }),
  // Ownership-checked on the server: Strapi's own `DELETE /api/upload/files/:id`
  // removed any file and is no longer granted to users.
  deleteFile: <T>(id: number): Promise<T> =>
    axios.delete(
      `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/user-uploads/${id}`,
      getAxiosHeaders()
    ),
  /**
   * Uploads one file straight away and returns the created record.
   *
   * Used by UploadField so a picked image becomes a real upload immediately:
   * the registration draft can then persist that record in localStorage, where
   * a live `File` handle could never be serialised. Deliberately sends no
   * `ref`/`refId` — the file is bound to the vendor at submit, by id.
   */
  uploadFile: (file: File, name?: string): Promise<StrapiImage> => {
    const formData = new FormData();
    formData.append('files', file, name ?? file.name);

    return axios
      .post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/upload`, formData, getAxiosHeaders())
      .then((response) => response.data[0]);
  },
  uploadEntryImages: (
    uploadMainImg,
    uploadImages,
    entity?: {
      entity: string;
      refId: string;
      field: string;
    }
  ) => {
    const formData = new FormData();

    const combinedUploadImages: Array<StrapiImage> = [];
    const filterImagesToUpload = uploadImages?.filter(
      (imageItem) => !!imageItem?.fileWithMeta?.file
    );

    if (filterImagesToUpload && filterImagesToUpload.length > 0) {
      combinedUploadImages.push(...filterImagesToUpload);
    }

    if (uploadMainImg && uploadMainImg?.fileWithMeta?.file) {
      combinedUploadImages.push(uploadMainImg);
    }

    const parsedFormDataImages = createImageUploadFormData(formData, combinedUploadImages, entity);

    if (combinedUploadImages.length === 0) {
      return Promise.resolve({ data: [] });
    }

    return axios.post(
      `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/upload`,
      parsedFormDataImages,
      getAxiosHeaders()
    );
  },
  geocodeFromLatLng: ({ lat, lng }) =>
    axios
      .post(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/geocode/geocodeFromLatLng`, {
        lat,
        lng
      })
      .then((response) => response)
      .catch((error) => error),
  getSendcloudCheckoutMethods: (shippingAddressId: number) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/sendcloud/checkout-methods`,
        { shippingAddressId },
        getAxiosHeaders()
      )
      .then((response) => response.data.data)
      .catch((error) => {
        Logger.error('Error fetching Sendcloud checkout methods:', error);
        throw error;
      }),
  createBasketCheckoutSession: (
    billingAddressId: number,
    shippingAddressId: number,
    remark?: string,
    sendcloudSelections?: Record<string, { optionCode: string; methodName: string; price: number }>
  ) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/create-checkout-session`,
        { billingAddressId, shippingAddressId, remark, sendcloudSelections },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        if (error?.response?.status === 409 && error.response.data?.error === 'BASKET_ADJUSTED') {
          return Promise.reject({
            isBasketAdjusted: true,
            adjustedItems: error.response.data.adjustedItems,
            message: error.response.data.message
          });
        }
        Logger.error('Error initiating checkout session:', error);
        return Promise.reject(error);
      }),
  cancelBasketCheckoutSession: () =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/cancel-checkout-session`,
        {},
        getAxiosHeaders()
      )
      .then((response) => response.data.data)
      .catch((error) => {
        Logger.error('Error cancelling checkout session:', error);
        return Promise.reject(error);
      }),
  createStripeSubscriptionSession: (vendorId: number, planId: number) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/create-subscription-session`,
        {
          vendorId,
          planId
        },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error initiating subscription session:', error);
        return Promise.reject(error);
      }),
  checkStripeSessionStatus: (sessionId: string) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/get-session-status`,
        { session_id: sessionId },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error checking Stripe session status:', error);
        return Promise.reject(error);
      }),
  createStripeExpressAccount: async (data?: any) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/create-express-account`,
        data,
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error creating Stripe Express account:', error);
        throw error;
      }),
  getStripeAccountStatus: async () =>
    axios
      .get(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/get-account-status`,
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error getting Stripe account status:', error);
        throw error;
      }),
  createStripeAccountLink: async ({ type, planId }) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/create-account-link`,
        {
          type,
          planId
        },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error creating Stripe account link:', error);
        throw error;
      }),
  createStripeCustomerPortalSession: async () =>
    axios
      .get(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/stripe/create-customer-portal-session`,
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error creating Stripe customer portal session:', error);
        throw error;
      }),
  getOrderBySessionId: async (sessionId: string) =>
    axios
      .get(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/orders/by-session/${sessionId}`,
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error fetching order by session ID:', error);
        throw error;
      }),
  updateOrderStatus: async (orderId: number, newStatus: string) =>
    axios
      .put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/orders/${orderId}/status`,
        { newStatus },
        getAxiosHeaders()
      )
      .then((response) => response.data as StrapiStatus<StrapiOrderStatusEnum>)
      .catch((error) => {
        Logger.error('Error updating order status:', error);
        throw error;
      }),
  updateVendorOrderStatus: async (
    vendorOrderId: number,
    newStatus: string,
    trackingNumber?: string,
    shippingCompany?: string
  ) =>
    axios
      .put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendor-orders/${vendorOrderId}/status`,
        { newStatus, trackingNumber, shippingCompany },
        getAxiosHeaders()
      )
      .then((response) => response.data as StrapiStatus<StrapiVendorOrderStatusEnum>)
      .catch((error) => {
        Logger.error('Error updating order status:', error);
        throw error;
      }),
  getPayoutsAdmin: async (page = 1, pageSize = 10) =>
    axios
      .get(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/admin/payouts?page=${page}&pageSize=${pageSize}`,
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error fetching payouts for admin:', error);
        throw error;
      }),
  getPayoutAdmin: async (id: number) =>
    axios
      .get(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/admin/payouts/${id}`, getAxiosHeaders())
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error fetching payout info for admin:', error);
        throw error;
      }),
  initiatePayoutAdmin: async (id: number) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/admin/payouts/${id}/initiate`,
        {},
        getAxiosHeaders()
      )
      .then((response) => response.data as StrapiStatus<string>)
      .catch((error) => {
        Logger.error('Error updating payout status for admin:', error);
        throw error;
      }),
  updateVendorPlan: async (newPlanId: string) =>
    axios
      .put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendors/plan/${newPlanId}`,
        {},
        getAxiosHeaders()
      )
      .then((response) => {
        const vendor = (response.data as { data: StrapiVendor }).data;
        const currentUser = regionStore.get(userAtom);
        const newUser = {
          ...currentUser,
          vendors: [vendor]
        } as StrapiUser;
        regionStore.set(userAtom, newUser);
        return true;
      })
      .catch((error) => {
        Logger.error('Error updating vendor plan:', error);
        throw error;
      }),
  updateSendcloudShipping: async (vendorId: number, enabled: boolean) =>
    axios
      .put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendors/sendcloud-shipping/${vendorId}`,
        { enabled },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error updating Sendcloud shipping:', error);
        throw error;
      }),
  getConversations: async (): Promise<{ data: ConversationsState }> =>
    axios
      .get(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/chat-conversations`, getAxiosHeaders())
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error fetching conversations:', error);
        throw error;
      }),
  getOrCreateConversation: async (vendorId: number): Promise<{ data: StrapiConversation }> =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/chat-conversations`,
        { vendorId },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error creating conversation:', error);
        throw error;
      }),
  getChatMessages: async (
    conversationId: number,
    page = 1,
    pageSize = 20
  ): Promise<{
    data: StrapiChatMessage[];
    meta: { page: number; pageSize: number; total: number };
  }> =>
    axios
      .get(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/chat-messages`, {
        params: { conversationId, page, pageSize },
        ...getAxiosHeaders()
      })
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error fetching chat messages:', error);
        throw error;
      }),
  sendChatMessage: async (
    conversationId: number,
    content: string
  ): Promise<{ data: StrapiChatMessage }> =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/chat-messages`,
        { conversationId, content },
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error sending chat message:', error);
        throw error;
      }),
  markConversationRead: async (conversationId: number): Promise<void> =>
    axios
      .put(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/chat-messages/read/${conversationId}`,
        {},
        getAxiosHeaders()
      )
      .then(() => undefined)
      .catch((error) => {
        Logger.error('Error marking conversation as read:', error);
        throw error;
      }),
  deleteAccount: async (): Promise<void> =>
    axios
      .delete(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/me`,
        getAxiosHeaders()
      )
      .then(() => undefined),
  // Reversible counterpart to deleteAccount. Same plugin-prefixed path family:
  // users-permissions extension routes mount under /api/users-permissions/*.
  deactivateAccount: async (): Promise<void> =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/me/deactivate`,
        {},
        getAxiosHeaders()
      )
      .then(() => undefined),
  reactivateAccount: async (): Promise<void> =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/users-permissions/users/me/reactivate`,
        {},
        getAxiosHeaders()
      )
      .then(() => undefined),
  previewVendorOrderLabel: async (vendorOrderId: number) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendor-orders/${vendorOrderId}/label/preview`,
        {},
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error previewing vendor order label:', error);
        throw error;
      }),
  confirmVendorOrderLabel: async (vendorOrderId: number) =>
    axios
      .post(
        `${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendor-orders/${vendorOrderId}/label/confirm`,
        {},
        getAxiosHeaders()
      )
      .then((response) => response.data)
      .catch((error) => {
        Logger.error('Error confirming vendor order label:', error);
        throw error;
      }),
  downloadVendorOrderLabel: async (vendorOrderId: number) =>
    axios
      .get(`${process.env.REACT_APP_STRAPI_ENDPOINT}/api/vendor-orders/${vendorOrderId}/label`, {
        ...getAxiosHeaders(),
        responseType: 'blob'
      })
      .then((response) => response.data as Blob)
      .catch((error) => {
        Logger.error('Error downloading vendor order label:', error);
        throw error;
      })
};

// Only used now to attach an explicit API token (registration/compat). The user
// session travels via the httpOnly cookie (withCredentials), so no-arg calls
// send no Authorization header.
const getAxiosHeaders = (token?: string): AxiosRequestConfig | undefined => {
  if (token) {
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  }

  return undefined;
};

// The session cookie is httpOnly, so logout must clear it server-side (keepalive
// so it survives the redirect that follows), then drop the Sentry user.
const removeCookie = (): void => {
  postLogout();
  setSentryUser(null);
};

const setUser = (newUser: StrapiUser): void => {
  const isAdmin = newUser?.role?.name === 'Admin';
  newUser.isAdmin = isAdmin;
  regionStore.set(userAtom, newUser);
  regionStore.set(authAtom, 'authenticated');
  setSentryUser({ id: newUser.id, email: newUser.email });
};

export interface SendcloudCheckoutMethod {
  code: string;
  name: string;
  carrier: string;
  price: number;
}

export interface SendcloudCheckoutVendorMethods {
  vendorTitle: string;
  totalWeight: number;
  methods: SendcloudCheckoutMethod[];
}

export type EntityPath = (typeof StrapiEntity)[keyof typeof StrapiEntity];

export { strapiClient, setUser, removeCookie };

export type StrapiClient = {
  register: (data: StrapiRegisterUserRequest) => Promise<string>;
  login: (email: string, password: string) => Promise<{ data: StrapiAuthenticationResponse }>;
  loadUser: (options?: { skipStripe?: boolean }) => Promise<any>;
  checkLogin: (email: string, password: string) => Promise<any>;
  delete: (id: number, entity: string) => Promise<void>;
  create: <T, S>(entityPath: EntityPath, entity: T, token?: string) => Promise<StrapiResponse<S>>;
  update: <T, S>(entityPath: EntityPath, id: number, entity: T) => Promise<StrapiResponse<S>>;
  updateUser: (updateData: Partial<StrapiUserRequest>) => Promise<StrapiUser | void>;
  fetch: <T>(request: StrapiRequest) => Promise<StrapiFetchResponse<T>>;
  deleteFile: (id: number) => Promise<void>;
  resetPassword: (email: string) => Promise<{ data: StrapiResetPasswordData }>;
  setNewPassword: (
    password: string,
    passwordConfirmation: string,
    code: string
  ) => Promise<{ data: StrapiAuthenticationResponse }>;
  changePassword: (
    currentPassword: string,
    password: string,
    passwordConfirmation: string
  ) => Promise<{ data: StrapiAuthenticationResponse }>;
  requestEmailChange: (email: string) => Promise<{ data: { data: { pendingEmail: string } } }>;
  confirmEmailChange: (token: string) => Promise<{ data: { data: { email: string } } }>;
  viesCheckStatus: () => Promise<boolean>;
  viesCheckVatNumber: (fullVatNumber: string) => Promise<StrapiResponse<any>>;
  sendRequestMail: (data: StrapiRequestMail) => Promise<void>;
  uploadFile: (file: File, name?: string) => Promise<StrapiImage>;
  uploadEntryImages: (
    mainImage?: StrapiImage,
    images?: Array<StrapiImage>,
    bindEntity?: {
      entity: string;
      refId: string;
      field: string;
    }
  ) => Promise<any>;
  geocodeFromLatLng: ({ lat, lng }: { lat: number; lng: number }) => Promise<GeocodeAddressState>;
  getSendcloudCheckoutMethods: (
    shippingAddressId: number
  ) => Promise<Record<string, SendcloudCheckoutVendorMethods>>;
  createBasketCheckoutSession: (
    billingAddressId: number,
    shippingAddressId: number,
    remark?: string,
    sendcloudSelections?: Record<string, { optionCode: string; methodName: string; price: number }>
  ) => Promise<{ clientSecret: string }>;
  cancelBasketCheckoutSession: () => Promise<{ released: boolean }>;
  createStripeSubscriptionSession: (
    vendorId: number,
    planId: number
  ) => Promise<{ clientSecret: string }>;
  checkStripeSessionStatus: (
    sessionId: string
  ) => Promise<{ status: string; customer_email: string }>;
  createStripeExpressAccount: (data?: any) => Promise<{ accountId: string; accountLink: string }>;
  getStripeAccountStatus: () => Promise<any>;
  createStripeAccountLink: ({
    type,
    planId
  }: {
    type: 'account_onboarding' | 'account_update';
    refreshUrl?: string;
    returnUrl?: string;
    planId?: string;
  }) => Promise<{ url: string }>;
  createStripeCustomerPortalSession: () => Promise<{ url: string }>;
  getOrderBySessionId: (sessionId: string) => Promise<StrapiResponse<StrapiOrder>>;
  updateOrderStatus: (
    orderId: number,
    newStatus: string
  ) => Promise<StrapiStatus<StrapiOrderStatusEnum>>;
  updateVendorOrderStatus: (
    vendorOrderId: number,
    newStatus: string,
    trackingNumber?: string,
    shippingCompany?: string
  ) => Promise<StrapiStatus<StrapiVendorOrderStatusEnum>>;
  getPayoutsAdmin: (
    page?: number,
    pageSize?: number
  ) => Promise<StrapiFetchResponse<StrapiPayout[]>>;
  getPayoutAdmin: (id: number) => Promise<StrapiResponse<StrapiPayout>>;
  initiatePayoutAdmin: (id: number) => Promise<StrapiStatus<string>>;
  updateVendorPlan: (newPlanId: string) => Promise<boolean>;
  updateSendcloudShipping: (
    vendorId: number,
    enabled: boolean
  ) => Promise<{ data: { id: number; useSendcloudShipping: boolean } }>;
  getConversations: () => Promise<{ data: ConversationsState }>;
  getOrCreateConversation: (vendorId: number) => Promise<{ data: StrapiConversation }>;
  getChatMessages: (
    conversationId: number,
    page?: number,
    pageSize?: number
  ) => Promise<{
    data: StrapiChatMessage[];
    meta: { page: number; pageSize: number; total: number };
  }>;
  sendChatMessage: (
    conversationId: number,
    content: string
  ) => Promise<{ data: StrapiChatMessage }>;
  markConversationRead: (conversationId: number) => Promise<void>;
  deleteAccount: () => Promise<void>;
  deactivateAccount: () => Promise<void>;
  reactivateAccount: () => Promise<void>;
  previewVendorOrderLabel: (vendorOrderId: number) => Promise<{
    data: {
      shippingMethod: { code: string; name: string; carrier: string } | null;
      estimatedCost: number | null;
      address: any;
      weight: number;
    };
  }>;
  confirmVendorOrderLabel: (vendorOrderId: number) => Promise<{
    data: {
      trackingNumber: string;
      trackingUrl: string;
      labelUrl: string | null;
    };
  }>;
  downloadVendorOrderLabel: (vendorOrderId: number) => Promise<Blob>;
};
